import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  createFixedPurchaseRule,
  pauseFixedPurchaseRule,
  updateFixedPurchaseRule,
} from '../lib/api';
import { cleanQuantity } from '../lib/calculation';
import { formatShortDate } from '../lib/date';
import {
  PURCHASE_DISPLAY_CATEGORY_OPTIONS,
  ingredientCategoryFromPurchaseDisplay,
  normalizeIngredientCategory,
  purchaseDisplayCategory,
} from '../lib/ingredient-categories';
import { isValidPurchaseQuantity, requiresIntegerQuantity } from '../lib/purchase-quantity';
import type {
  Dish,
  FixedPurchaseRule,
  FixedPurchaseRuleInput,
  FixedPurchaseScheduleType,
  FixedPurchaseSourceType,
  IngredientCatalogItem,
  IngredientCategory,
} from '../types';
import { CheckIcon, ChevronDownIcon, ChevronRightIcon, EditIcon, PlusIcon, SearchIcon } from './Icons';
import { Modal } from './Modal';

const UNITS = ['斤', '两', '公斤', '克', '只', '个', '包', '袋', '瓶', '桶', '箱', '板', '把', '件', '升', '毫升'];
const WEEKDAYS = [
  { bit: 1, label: '周一' },
  { bit: 2, label: '周二' },
  { bit: 3, label: '周三' },
  { bit: 4, label: '周四' },
  { bit: 5, label: '周五' },
  { bit: 6, label: '周六' },
  { bit: 0, label: '周日' },
];

interface Props {
  open: boolean;
  token: string;
  rules: FixedPurchaseRule[];
  ingredients: IngredientCatalogItem[];
  dishes: Dish[];
  defaultEffectiveFromWeek?: string;
  onClose: () => void;
  onRulesChange: (rules: FixedPurchaseRule[], revision: number) => void;
  onNotify: (message: string, tone?: 'success' | 'error' | 'info') => void;
}

interface RuleDraft {
  sourceType: FixedPurchaseSourceType;
  sourceId: string;
  name: string;
  unit: string;
  ingredientCategory: IngredientCategory;
  quantity: string;
  batchCount: number;
  quantityMode: boolean;
  scheduleType: FixedPurchaseScheduleType;
  weekdayMask: number;
}

function emptyDraft(): RuleDraft {
  return {
    sourceType: 'ingredient',
    sourceId: '',
    name: '',
    unit: '斤',
    ingredientCategory: '素菜',
    quantity: '',
    batchCount: 1,
    quantityMode: false,
    scheduleType: 'service_day',
    weekdayMask: 62,
  };
}

function draftFromRule(rule: FixedPurchaseRule): RuleDraft {
  return {
    sourceType: rule.sourceType,
    sourceId: rule.sourceId ?? '',
    name: rule.name,
    unit: rule.unit ?? (rule.sourceSnapshot.sourceType === 'dish' ? '' : rule.sourceSnapshot.unit),
    ingredientCategory: normalizeIngredientCategory(rule.ingredientCategory, rule.name),
    quantity: rule.quantity ?? '',
    batchCount: rule.batchCount ?? 1,
    quantityMode: rule.quantity !== undefined,
    scheduleType: rule.scheduleType,
    weekdayMask: rule.weekdayMask ?? 62,
  };
}

export function fixedPurchaseScheduleLabel(rule: Pick<FixedPurchaseRule, 'scheduleType' | 'weekdayMask'>): string {
  if (rule.scheduleType === 'weekly') return '每周自动加入一次';
  if (rule.scheduleType === 'service_day') return '每个供餐日';
  const mask = rule.weekdayMask ?? 0;
  const labels = WEEKDAYS.filter((day) => (mask & (1 << day.bit)) !== 0).map((day) => day.label);
  return `${labels.join('、') || '未选择星期'}（供餐时）`;
}

function multiplyQuantity(quantity: string, factor: number): string {
  return cleanQuantity(Number(quantity) * factor);
}

export function fixedPurchaseAmountLabel(rule: FixedPurchaseRule): string {
  if (rule.quantity !== undefined) return `${rule.quantity}${rule.unit ?? ''}`;
  if (rule.sourceType !== 'dish') return `—${rule.unit ?? ''}`;
  if (rule.sourceSnapshot.sourceType === 'dish' && rule.sourceSnapshot.ingredients.length === 1) {
    const ingredient = rule.sourceSnapshot.ingredients[0];
    return `${multiplyQuantity(ingredient.quantity, rule.batchCount ?? 1)}${ingredient.unit}`;
  }
  return rule.batchCount && rule.batchCount > 1 ? `${rule.batchCount}批配方` : '完整配方';
}

function FixedRuleRow({
  rule,
  onEdit,
  onPause,
  onResume,
  busy,
}: {
  rule: FixedPurchaseRule;
  onEdit: () => void;
  onPause: () => void;
  onResume: () => void;
  busy: boolean;
}) {
  return (
    <article className={`fixed-rule-row ${rule.active ? '' : 'is-paused'}`}>
      <button className="fixed-rule-row__main" type="button" onClick={onEdit} aria-label={`修改固定采购${rule.name}`}>
        <span className="fixed-rule-row__copy">
          <small>{fixedPurchaseScheduleLabel(rule)}</small>
          <span className="fixed-rule-row__equal">
            <strong>{rule.name}</strong>
            <strong>{fixedPurchaseAmountLabel(rule)}</strong>
          </span>
          <small>{rule.sourceType === 'dish' ? '按菜品配方采购' : `${purchaseDisplayCategory(rule.ingredientCategory, rule.name)} · 直接采购食材`}</small>
        </span>
        <ChevronRightIcon size={20} />
      </button>
      <button
        className={`button button--small ${rule.active ? 'button--ghost fixed-rule-row__pause' : 'button--soft'}`}
        type="button"
        disabled={busy}
        onClick={rule.active ? onPause : onResume}
      >
        {rule.active ? '停用' : '恢复使用'}
      </button>
    </article>
  );
}

export function FixedPurchaseSettings({
  open,
  token,
  rules,
  ingredients,
  dishes,
  defaultEffectiveFromWeek,
  onClose,
  onRulesChange,
  onNotify,
}: Props) {
  const [editing, setEditing] = useState<FixedPurchaseRule | 'new' | null>(null);
  const [draft, setDraft] = useState<RuleDraft>(emptyDraft);
  const [query, setQuery] = useState('');
  const [sourcePickerOpen, setSourcePickerOpen] = useState(false);
  const [pausedRulesOpen, setPausedRulesOpen] = useState(true);
  const [busyRuleId, setBusyRuleId] = useState('');
  const [error, setError] = useState('');

  const activeRules = rules.filter((rule) => rule.active);
  const dayRules = activeRules.filter((rule) => rule.scheduleType !== 'weekly');
  const weeklyRules = activeRules.filter((rule) => rule.scheduleType === 'weekly');
  const pausedRules = rules.filter((rule) => !rule.active);

  useEffect(() => {
    if (open) setPausedRulesOpen(true);
  }, [open, pausedRules.length]);

  const filteredIngredients = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('zh-CN');
    return ingredients.filter((item) => !normalized
      || `${item.name} ${item.unit} ${item.ingredientCategory} ${purchaseDisplayCategory(item.ingredientCategory, item.name)}`.toLocaleLowerCase('zh-CN').includes(normalized))
      .slice(0, 30);
  }, [ingredients, query]);
  const filteredDishes = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('zh-CN');
    return dishes.filter((item) => item.active && (!normalized
      || `${item.name} ${item.ingredients.map((line) => line.name).join(' ')}`.toLocaleLowerCase('zh-CN').includes(normalized)))
      .slice(0, 30);
  }, [dishes, query]);

  const startNew = () => {
    setDraft(emptyDraft());
    setQuery('');
    setSourcePickerOpen(true);
    setError('');
    setEditing('new');
  };

  const startEdit = (rule: FixedPurchaseRule) => {
    setDraft(draftFromRule(rule));
    setQuery('');
    setSourcePickerOpen(false);
    setError('');
    setEditing(rule);
  };

  const selectIngredient = (item: IngredientCatalogItem) => {
    setDraft((current) => ({
      ...current,
      sourceId: item.id,
      name: item.name,
      unit: item.unit,
      ingredientCategory: item.ingredientCategory,
    }));
    setQuery('');
    setSourcePickerOpen(false);
  };

  const selectDish = (dish: Dish) => {
    const single = dish.ingredients.length === 1 ? dish.ingredients[0] : undefined;
    setDraft((current) => ({
      ...current,
      sourceId: dish.id,
      name: dish.name,
      quantityMode: Boolean(single),
      quantity: single?.quantity ?? '',
      unit: single?.unit ?? '',
      ingredientCategory: normalizeIngredientCategory(single?.ingredientCategory, single?.name),
    }));
    setQuery('');
    setSourcePickerOpen(false);
  };

  const setSourceType = (sourceType: FixedPurchaseSourceType) => {
    setDraft({ ...emptyDraft(), sourceType });
    setQuery('');
    setSourcePickerOpen(sourceType !== 'custom');
    setError('');
  };

  const toggleWeekday = (bit: number) => {
    setDraft((current) => ({
      ...current,
      weekdayMask: current.weekdayMask ^ (1 << bit),
    }));
  };

  const saveRule = async (event: FormEvent) => {
    event.preventDefault();
    const direct = draft.sourceType !== 'dish' || draft.quantityMode;
    if (draft.sourceType !== 'custom' && !draft.sourceId) {
      setError(draft.sourceType === 'dish' ? '请先选择菜品' : '请先选择食材');
      return;
    }
    if (draft.sourceType === 'custom' && !draft.name.trim()) {
      setError('请输入食材名称');
      return;
    }
    if (direct && !isValidPurchaseQuantity(draft.quantity, draft.unit)) {
      setError(requiresIntegerQuantity(draft.unit) ? '该单位需要填写大于0的整数' : '请输入大于0的采购数量');
      return;
    }
    if (draft.sourceType === 'dish' && (!Number.isInteger(draft.batchCount) || draft.batchCount < 1 || draft.batchCount > 99)) {
      setError('菜品批次必须为1到99的整数');
      return;
    }
    if (draft.scheduleType === 'weekdays' && draft.weekdayMask === 0) {
      setError('请至少选择一个采购日');
      return;
    }

    const input: FixedPurchaseRuleInput = {
      sourceType: draft.sourceType,
      ...(draft.sourceType !== 'custom' ? { sourceId: draft.sourceId } : {
        name: draft.name.trim(),
        unit: draft.unit.trim(),
        ingredientCategory: draft.ingredientCategory,
      }),
      scheduleType: draft.scheduleType,
      ...(draft.scheduleType === 'weekdays' ? { weekdayMask: draft.weekdayMask } : {}),
      ...(direct ? { quantity: draft.quantity.trim() } : { batchCount: draft.batchCount }),
      ...(defaultEffectiveFromWeek ? { effectiveFromWeek: defaultEffectiveFromWeek } : {}),
    };

    const ruleId = editing === 'new' ? 'new' : editing?.id ?? '';
    setBusyRuleId(ruleId);
    setError('');
    try {
      const result = editing === 'new'
        ? await createFixedPurchaseRule(token, input)
        : await updateFixedPurchaseRule(token, editing!.id, {
            ...input,
            expectedRevision: editing!.revision,
          });
      const nextRules = editing === 'new'
        ? [...rules, result.rule]
        : rules.map((rule) => rule.id === result.rule.id ? result.rule : rule);
      onRulesChange(nextRules, result.rulesRevision);
      setEditing(null);
      onNotify(
        `${result.rule.name}固定规则已保存${result.rule.effectiveFromWeek ? `，从${formatShortDate(result.rule.effectiveFromWeek)}这周起生效` : ''}`,
        'success',
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '固定采购规则保存失败');
    } finally {
      setBusyRuleId('');
    }
  };

  const pauseRule = async (rule: FixedPurchaseRule) => {
    if (!window.confirm(`确认停用“${rule.name}”固定采购吗？已经确认的历史采购单不会改变。`)) return;
    setBusyRuleId(rule.id);
    try {
      const result = await pauseFixedPurchaseRule(
        token,
        rule.id,
        rule.revision,
        defaultEffectiveFromWeek,
      );
      onRulesChange(rules.map((item) => item.id === rule.id ? result.rule : item), result.rulesRevision);
      onNotify(`${rule.name}固定采购已停用`, 'success');
    } catch (reason) {
      onNotify(reason instanceof Error ? reason.message : '停用失败，请重试', 'error');
    } finally {
      setBusyRuleId('');
    }
  };

  const resumeRule = async (rule: FixedPurchaseRule) => {
    setBusyRuleId(rule.id);
    try {
      const result = await updateFixedPurchaseRule(token, rule.id, {
        active: true,
        expectedRevision: rule.revision,
        effectiveFromWeek: defaultEffectiveFromWeek,
      });
      onRulesChange(rules.map((item) => item.id === rule.id ? result.rule : item), result.rulesRevision);
      onNotify(`${rule.name}固定采购已恢复`, 'success');
    } catch (reason) {
      onNotify(reason instanceof Error ? reason.message : '恢复失败，请重试', 'error');
    } finally {
      setBusyRuleId('');
    }
  };

  const close = () => {
    if (editing) {
      setEditing(null);
      setError('');
      return;
    }
    onClose();
  };

  const rulesPage = (
    <div className="fixed-rules-page" data-qa="fixed-purchase-rules-list">
      <div className="inline-alert fixed-rules-page__notice">
        <CheckIcon size={20} />
        <span><strong>这是长期设置</strong><small>修改后影响以后每周；历史采购单不会改变。</small></span>
      </div>

      <section className="fixed-rules-group">
        <header><strong>按日期自动加入</strong><span>{dayRules.length} 项</span></header>
        {dayRules.length ? dayRules.map((rule) => <FixedRuleRow rule={rule} key={rule.id} busy={busyRuleId === rule.id} onEdit={() => startEdit(rule)} onPause={() => void pauseRule(rule)} onResume={() => void resumeRule(rule)} />) : <p className="fixed-rules-empty">还没有按日期自动采购的项目。</p>}
      </section>
      <section className="fixed-rules-group">
        <header><strong>每周自动加入一次</strong><span>{weeklyRules.length} 项</span></header>
        {weeklyRules.length ? weeklyRules.map((rule) => <FixedRuleRow rule={rule} key={rule.id} busy={busyRuleId === rule.id} onEdit={() => startEdit(rule)} onPause={() => void pauseRule(rule)} onResume={() => void resumeRule(rule)} />) : <p className="fixed-rules-empty">还没有每周自动采购的项目。</p>}
      </section>
      {pausedRules.length > 0 && (
        <details
          className="fixed-rules-paused"
          open={pausedRulesOpen}
        >
          <summary
            aria-expanded={pausedRulesOpen}
            onClick={(event) => {
              event.preventDefault();
              setPausedRulesOpen((current) => !current);
            }}
          >
            <span>已停用 {pausedRules.length} 项</span>
            <span className="fixed-rules-paused__action">
              {pausedRulesOpen ? '收起列表' : '展开查看'}
              <ChevronDownIcon size={20} />
            </span>
          </summary>
          <div>{pausedRules.map((rule) => <FixedRuleRow rule={rule} key={rule.id} busy={busyRuleId === rule.id} onEdit={() => startEdit(rule)} onPause={() => void pauseRule(rule)} onResume={() => void resumeRule(rule)} />)}</div>
        </details>
      )}
      <button className="button button--primary fixed-rules-page__add" type="button" onClick={startNew}><PlusIcon size={21} />新增固定采购</button>
    </div>
  );

  const ruleForm = editing ? (
    <form className="fixed-rule-form" data-qa="fixed-purchase-rule-form" onSubmit={saveRule}>
          <div className="inline-alert fixed-rule-form__notice">
            <EditIcon size={20} />
            <span>这里修改长期规则，已经确认的历史采购单不会改变。</span>
          </div>

          <fieldset className="fixed-rule-form__section">
            <legend>采购方式</legend>
            <div className="fixed-rule-form__choice-grid fixed-rule-form__choice-grid--three">
              {([
                ['ingredient', '直接选食材'],
                ['dish', '按菜品配方'],
                ['custom', '自定义食材'],
              ] as const).map(([value, label]) => (
                <button
                  className={draft.sourceType === value ? 'is-active' : ''}
                  type="button"
                  aria-pressed={draft.sourceType === value}
                  onClick={() => setSourceType(value)}
                  key={value}
                >
                  {draft.sourceType === value && <CheckIcon size={18} />}{label}
                </button>
              ))}
            </div>
          </fieldset>

          {draft.sourceType !== 'custom' ? (
            <fieldset className="fixed-rule-form__section">
              <legend>{draft.sourceType === 'dish' ? '选择菜品' : '选择食材'}</legend>
              {draft.name && (
                <div className="fixed-rule-form__selected" aria-live="polite">
                  <span><CheckIcon size={19} /><strong>已选：{draft.name}</strong></span>
                  <button
                    type="button"
                    aria-expanded={sourcePickerOpen}
                    onClick={() => {
                      setQuery('');
                      setSourcePickerOpen((current) => !current);
                    }}
                  >
                    {sourcePickerOpen ? '收起列表' : `更换${draft.sourceType === 'dish' ? '菜品' : '食材'}`}
                  </button>
                </div>
              )}
              {(!draft.sourceId || sourcePickerOpen) && (
                <>
                  <label className="fixed-rule-form__search">
                    <SearchIcon size={21} />
                    <input
                      aria-label={draft.sourceType === 'dish' ? '搜索固定采购菜品' : '搜索固定采购食材'}
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder={draft.sourceType === 'dish' ? '搜索菜品' : '搜索食材'}
                    />
                  </label>
                  <div className="fixed-rule-source-list" role="list">
                    {draft.sourceType === 'dish'
                      ? filteredDishes.map((dish) => (
                          <button className={draft.sourceId === dish.id ? 'is-selected' : ''} type="button" onClick={() => selectDish(dish)} key={dish.id}>
                            <span><strong>{dish.name}</strong><small>{dish.ingredients.map((item) => item.name).join('、')}</small></span>
                            {draft.sourceId === dish.id && <CheckIcon size={20} />}
                          </button>
                        ))
                      : filteredIngredients.map((item) => (
                          <button className={draft.sourceId === item.id ? 'is-selected' : ''} type="button" onClick={() => selectIngredient(item)} key={item.id}>
                            <span><strong>{item.name}</strong><small>{purchaseDisplayCategory(item.ingredientCategory, item.name)} · {item.unit}</small></span>
                            {draft.sourceId === item.id && <CheckIcon size={20} />}
                          </button>
                        ))}
                  </div>
                </>
              )}
            </fieldset>
          ) : (
            <fieldset className="fixed-rule-form__section">
              <legend>自定义食材</legend>
              <label className="field"><span>食材名称</span><input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></label>
              <label className="field"><span>采购分类</span><select value={purchaseDisplayCategory(draft.ingredientCategory, draft.name)} onChange={(event) => setDraft({ ...draft, ingredientCategory: ingredientCategoryFromPurchaseDisplay(event.target.value as ReturnType<typeof purchaseDisplayCategory>) })}>{PURCHASE_DISPLAY_CATEGORY_OPTIONS.map((category) => <option value={category} key={category}>{category}</option>)}</select></label>
            </fieldset>
          )}

          <fieldset className="fixed-rule-form__section">
            <legend>{draft.sourceType === 'dish' ? '菜品用量' : '每次采购数量'}</legend>
            {draft.sourceType === 'dish' && !draft.quantityMode ? (
              <label className="field"><span>批次数量（低频调整）</span><input aria-label="固定菜品批次数量" type="number" inputMode="numeric" min="1" max="99" step="1" value={draft.batchCount} onChange={(event) => setDraft({ ...draft, batchCount: Number(event.target.value) })} /></label>
            ) : (
              <div className="fixed-rule-form__quantity">
                <label className="field"><span>数量</span><input aria-label="固定采购数量" type="number" inputMode={requiresIntegerQuantity(draft.unit) ? 'numeric' : 'decimal'} min="0.001" step="any" value={draft.quantity} onChange={(event) => setDraft({ ...draft, quantity: event.target.value })} /></label>
                <label className="field"><span>单位</span><select value={draft.unit} disabled={draft.sourceType === 'ingredient'} onChange={(event) => setDraft({ ...draft, unit: event.target.value })}>{UNITS.map((unit) => <option value={unit} key={unit}>{unit}</option>)}</select></label>
              </div>
            )}
          </fieldset>

          <fieldset className="fixed-rule-form__section">
            <legend>什么时候自动加入</legend>
            <div className="fixed-rule-form__choice-grid">
              {([
                ['service_day', '每个供餐日'],
                ['weekdays', '指定星期'],
                ['weekly', '每周一次'],
              ] as const).map(([value, label]) => (
                <button className={draft.scheduleType === value ? 'is-active' : ''} type="button" aria-pressed={draft.scheduleType === value} onClick={() => setDraft({ ...draft, scheduleType: value })} key={value}>{draft.scheduleType === value && <CheckIcon size={18} />}{label}</button>
              ))}
            </div>
            {draft.scheduleType === 'weekdays' && (
              <div className="fixed-rule-form__weekdays" aria-label="选择固定采购星期">
                {WEEKDAYS.map((day) => {
                  const selected = (draft.weekdayMask & (1 << day.bit)) !== 0;
                  return <button type="button" className={selected ? 'is-active' : ''} aria-pressed={selected} onClick={() => toggleWeekday(day.bit)} key={day.bit}>{day.label}</button>;
                })}
              </div>
            )}
            {draft.scheduleType !== 'weekly' && <p className="fixed-rule-form__help">当天早餐和中午都停餐时，系统不会加入这项采购。</p>}
          </fieldset>

          <div className="fixed-rule-form__summary">
            <strong>保存后</strong>
            <span>{draft.name || '该项目'} {draft.sourceType === 'dish' && !draft.quantityMode ? `${draft.batchCount}批` : `${draft.quantity || '—'}${draft.unit}`} · {fixedPurchaseScheduleLabel(draft)}</span>
            {defaultEffectiveFromWeek && <small>默认从 {formatShortDate(defaultEffectiveFromWeek)} 这周起生效</small>}
          </div>
          {error && <p className="form-error" role="alert">{error}</p>}
          <div className="fixed-rule-form__actions">
            <button className="button button--ghost" type="button" onClick={() => setEditing(null)}>取消</button>
            <button className="button button--primary" type="submit" disabled={Boolean(busyRuleId)}>{busyRuleId ? '正在保存…' : '保存固定规则'}</button>
          </div>
    </form>
  ) : (
    <div className="fixed-rules-guide" data-qa="fixed-purchase-rules-guide">
      <span className="fixed-rules-guide__icon"><CheckIcon size={28} /></span>
      <h3>先从左侧选择一项</h3>
      <p>可修改固定数量、自动加入日期，或停用不再采购的项目。</p>
      <dl>
        <div><dt>长期规则</dt><dd>默认从下一未确认周起生效</dd></div>
        <div><dt>本周临时变化</dt><dd>请回到周计划使用“调整本周固定采购”</dd></div>
        <div><dt>历史采购单</dt><dd>始终保持原确认版本，不会被改写</dd></div>
      </dl>
    </div>
  );

  return (
    <Modal
      open={open}
      title={editing ? (editing === 'new' ? '新增固定采购' : `修改${editing.name}`) : '固定采购设置'}
      eyebrow={editing ? '长期规则' : '以后每周自动加入'}
      size="large"
      variant="mobile-page"
      onClose={close}
    >
      <div
        className={`fixed-rules-workspace${editing ? ' is-editing' : ''}`}
        data-qa="fixed-purchase-settings"
      >
        <aside className="fixed-rules-workspace__list">{rulesPage}</aside>
        <section className="fixed-rules-workspace__detail" aria-label={editing ? '编辑长期固定规则' : '固定采购说明'}>
          {ruleForm}
        </section>
      </div>
    </Modal>
  );
}
